/**
 * useFileUpload Hook
 *
 * Manages file upload operations including Firebase Storage uploads,
 * platform-specific media uploads, and file validation state management.
 */

import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import firebase from 'firebase/compat/app';
import Mime from 'mime';

// Models
import { Room, Collaboration } from 'src/app/models/Chat';
import { chatFiles } from 'src/app/models/Files';
import Account from 'src/app/models/Account';

// Database
import { addChatFiles, getFileByMediaSHA256 } from 'src/db';
import { createRef } from 'src/db/connection';
import { uploadFileToStorage } from 'src/api/firebase/storage';
import { getAccess } from 'src/db/serviceApiSetting';

// Utils
import { calculateFileSHA256 } from 'src/util/FileUtil';
import {
  createFileDataByUploadResult,
  getVideoCover,
  getVideoDetails,
} from 'src/util/video';
import { checkFile, formatSize, validateImageDimensions } from 'src/app/modules/chat/utils';
import * as lc from 'src/app/modules/localstorage/index';
import { env } from 'src/config/env';
import { ThumbnailUtil } from 'src/utils/thumbnailUtil';

// Types
type FileMediaData =
  | (chatFiles & { id?: string })
  | { url?: string; id?: string };

export interface UseFileUploadParams {
  selectedRoom: Room | undefined;
  selectedCollaboration: Collaboration;
  account?: Account; // Optional account for marketplace shop ID
}

export interface UseFileUploadReturn {
  file: File | undefined;
  fileSize: string;
  inputFileName: string;
  messageType: string | undefined;
  generatedThumbnail: string | null;
  isHEIC: boolean;
  setFile: (file: File | undefined) => void;
  setFileSize: (size: string) => void;
  setInputFileName: (name: string) => void;
  setMessageType: (type: string | undefined) => void;
  setGeneratedThumbnail: (thumbnail: string | null) => void;
  uploadFile: (companyID: string) => Promise<FileMediaData | undefined>;
  uploadMediaFallback: () => Promise<{ url?: string; id?: string } | undefined>;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  removeFile: () => void;
}

export const useFileUpload = ({
  selectedRoom,
  selectedCollaboration,
  account,
}: UseFileUploadParams): UseFileUploadReturn => {
  const { t } = useTranslation();

  // File state
  const [file, setFile] = useState<File>();
  const [fileSize, setFileSize] = useState<string>('');
  const [inputFileName, setInputFileName] = useState<string>('');
  const [messageType, setMessageType] = useState<string | undefined>('text');
  const [generatedThumbnail, setGeneratedThumbnail] = useState<string | null>(null);
  const [isHEIC, setIsHEIC] = useState<boolean>(false);

  /**
   * Build platform-specific media upload endpoint (v2 API format)
   */
  const buildMediaUploadEndpoint = useCallback((): {
    endpoint: string | null;
    platform: string;
  } => {
    const channel = selectedRoom?.channel;
    if (!channel) return { endpoint: null, platform: '' };

    const platform = channel.toLowerCase();

    // Extract platform-specific sender/identifier based on channel type
    let sender: string | null = null;

    switch (platform) {
      case 'whatsapp':
        // WhatsApp: use phone number as sender
        sender =
          selectedRoom?.userSearchKey ||
          selectedCollaboration?.phoneNumber ||
          null;
        break;

      case 'shopee':
      case 'lazada':
      case 'tiktok':
        // Marketplaces: use marketplaceShopId from account (same as send message)
        sender = account?.marketplaceShopId || null;
        console.log(`[useFileUpload] ${platform} sender determination:`, {
          marketplaceShopId: account?.marketplaceShopId,
          finalSender: sender,
        });
        break;

      default:
        sender = null;
    }

    const endpoint = sender ? `/v2/${platform}/${sender}/media` : null;
    console.log(`[useFileUpload] Media upload endpoint for ${platform}:`, endpoint);
    return { endpoint, platform };
  }, [
    selectedRoom?.channel,
    selectedRoom?.userSearchKey,
    selectedCollaboration?.phoneNumber,
    account?.marketplaceShopId,
  ]);

  /**
   * Platform media upload fallback (when Firebase fails)
   */
  const uploadMediaFallback = useCallback(async (): Promise<
    { url?: string; id?: string } | undefined
  > => {
    if (!file) {
      console.warn('[useFileUpload] uploadMediaFallback: No file to upload');
      return undefined;
    }

    const { endpoint, platform } = buildMediaUploadEndpoint();
    if (!endpoint) {
      console.error(
        '[useFileUpload] Could not determine media upload endpoint for channel:',
        selectedRoom?.channel,
        '- sender ID might be missing'
      );
      return undefined;
    }

    try {
      // Get access token (same as message sending)
      const apiAccess = await getAccess();
      if (!apiAccess?.accessToken) {
        console.error('[useFileUpload] No access token found');
        return undefined;
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('messaging_product', platform);
      formData.append('type', file.type.split('/')[0]); // image, video, audio, document

      // Build full server URL (same pattern as message sending)
      const fullUrl = `${env.SERVER_URL}${endpoint}`;

      console.log(`[useFileUpload] Uploading ${file.type} to server endpoint: ${fullUrl}`);
      console.log(`[useFileUpload] File details:`, {
        name: file.name,
        size: file.size,
        type: file.type,
      });

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${apiAccess.accessToken}`,
        },
        body: formData,
      });

      console.log(`[useFileUpload] Upload response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          `[useFileUpload] Platform media upload failed (${response.status}):`,
          response.statusText,
          '\nResponse body:',
          errorText
        );
        return undefined;
      }

      const data = await response.json();
      console.log('[useFileUpload] Upload response data:', JSON.stringify(data, null, 2));

      // Map response based on platform response format
      // Normalize all platform responses to { url, id }
      const mediaUrl =
        data.response?.mediaUrl ||
        data.response?.url ||
        data.mediaUrl ||
        data.url ||
        undefined;

      const fileId = data.response?.id || data.id || undefined;

      console.log('[useFileUpload] Platform media upload successful:', {
        platform,
        mediaUrl,
        fileId,
        hasUrl: !!mediaUrl,
        hasId: !!fileId
      });

      if (!mediaUrl) {
        console.warn('[useFileUpload] Upload succeeded but no mediaUrl in response!');
      }

      return { url: mediaUrl, id: fileId };
    } catch (error) {
      console.error('[useFileUpload] Platform media upload error:', error);
      return undefined;
    }
  }, [file, buildMediaUploadEndpoint, selectedRoom?.channel]);

  /**
   * File upload handler (Firebase Storage)
   */
  const uploadFile = useCallback(
    async (companyID: string) => {
      if (!file) return;

      const clientID = lc.getItemLC(lc.LCName.Client)?.id;
      if (!clientID) {
        alert('client Id not found');
        setInputFileName('client Id not found');
        setFile(undefined);
        return;
      }

      const fileSHA256 = await calculateFileSHA256(file);
      const fileChannel = selectedRoom?.channel;

      if (!fileSHA256 || !fileChannel) return;

      const fileExist = await getFileByMediaSHA256(
        fileSHA256,
        fileChannel,
        clientID
      );
      if (fileExist) return fileExist;

      const fileName = `${file.name.split('.')[0]}_${Date.now()}`;
      const fileMimeType = file.type;
      const fileExtension = Mime.getExtension(fileMimeType);
      const fileTypeRaw = file.type.split('/')[0];
      const fileType =
        fileTypeRaw === 'image' ||
        fileTypeRaw === 'video' ||
        fileTypeRaw === 'audio'
          ? fileTypeRaw
          : 'document';

      const storagePath = `${clientID}/${fileType}s/sent/${fileName}${
        fileExtension ? `.${fileExtension}` : ''
      }`;
      const metadata = { contentType: fileMimeType, fileName };

      const uploadResult = await uploadFileToStorage(
        storagePath,
        file,
        metadata
      );

      if (!uploadResult) {
        // Firebase upload failed - return undefined so calling code can handle via API
        console.warn(
          'Firebase storage upload failed, file will be processed by API'
        );
        return undefined;
      }

      const FileDataChat: chatFiles = {
        client: createRef('clients', clientID),
        name: uploadResult.metaData.name,
        url: uploadResult.mediaUrl,
        storage_path: uploadResult.metaData.fullPath,
        size: uploadResult.metaData.size,
        type: fileType,
        media_channel: fileChannel,
        media_sha256: fileSHA256,
        mime_type: uploadResult.metaData.contentType ?? fileMimeType ?? '',
        extension: fileExtension ?? '',
        createdAt: firebase.firestore.Timestamp.now(),
        updatedAt: firebase.firestore.Timestamp.now(),
      };

      if (fileType === 'video') {
        const videoDetail = await getVideoDetails(file);
        const videoCover = await getVideoCover(file);
        if (videoCover) {
          const coverFile = await createFileDataByUploadResult(videoCover, {
            clientId: clientID,
            channel: FileDataChat.media_channel,
          });
          if (coverFile) {
            FileDataChat.media_preview_id = coverFile.id;
            FileDataChat.media_width = videoDetail.width;
            FileDataChat.media_height = videoDetail.height;
          }
        }
      }

      const addFileResult = await addChatFiles(FileDataChat);
      return { ...FileDataChat, id: addFileResult?.id };
    },
    [file, selectedRoom?.channel, t]
  );

  /**
   * File select handler
   */
  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = event.target.files?.[0];
      if (selectedFile) {
        // Check if file is HEIC format
        const isHeicFile =
          selectedFile.type === 'image/heic' ||
          selectedFile.type === 'image/heif' ||
          selectedFile.name.toLowerCase().endsWith('.heic') ||
          selectedFile.name.toLowerCase().endsWith('.heif');

        if (isHeicFile) {
          // HEIC file detected - skip thumbnail generation (can't decode in browser)
          console.log('[useFileUpload] HEIC file detected:', selectedFile.name);
          setFile(selectedFile);
          setIsHEIC(true);
          setGeneratedThumbnail(null);
          return;
        }

        setFile(selectedFile);
        setIsHEIC(false);

        // Generate thumbnail immediately for images (before validation)
        if (selectedFile.type.startsWith('image/')) {
          try {
            console.log('[useFileUpload] Generating thumbnail for:', selectedFile.name);
            const thumbnail = await ThumbnailUtil.generateThumbnail(selectedFile);
            if (thumbnail) {
              console.log('[useFileUpload] Thumbnail generated successfully, size:', thumbnail.length);
              setGeneratedThumbnail(thumbnail);
            } else {
              console.warn('[useFileUpload] Thumbnail generation returned null');
            }
          } catch (error) {
            console.error('[useFileUpload] Thumbnail generation failed:', error);
            setGeneratedThumbnail(null);
          }
        }
      }
    },
    []
  );

  /**
   * Remove file handler
   */
  const removeFile = useCallback(() => {
    setFile(undefined);
    setFileSize('');
    setInputFileName('');
    setMessageType('text');
    setGeneratedThumbnail(null);
    setIsHEIC(false);
  }, []);

  /**
   * File validation effect
   * Validates file type, size, and extension when file changes
   */
  useEffect(() => {
    if (file) {
      const size = file.size;
      setInputFileName(file.name);
      setFileSize(formatSize(size));

      const tempArrFileType = file.type;
      const arryFileType = tempArrFileType.split('/');

      if (!arryFileType?.length || !arryFileType[0]) {
        alert(t('HC.Error.InvalidFiletype'));
        setInputFileName(t('HC.Error.InvalidFiletype'));
        setFile(undefined);
        return;
      }

      const fileType = arryFileType[0];
      const fileExtension = arryFileType[1];
      const [result, error, maxSize] = checkFile(
        fileType,
        fileExtension,
        size,
        selectedRoom?.channel
      );

      if (error) {
        const errorMsg =
          maxSize !== 0
            ? t('HC.File.' + error).replace('<<size>>', maxSize + ' MB')
            : t('HC.Error.' + error);
        alert(errorMsg);
        setInputFileName(errorMsg);
        setFile(undefined);
        return;
      }

      if (result) {
        // Phase 2: Validate image dimensions before setting message type
        if (result === 'image') {
          const img = new Image();
          img.onload = () => {
            const dimensionError = validateImageDimensions(img.width, img.height);
            if (dimensionError) {
              const errorMsg = t('HC.Error.' + dimensionError);
              alert(errorMsg);
              setInputFileName(errorMsg);
              setFile(undefined);
              setGeneratedThumbnail(null); // Clear thumbnail if validation fails
              return;
            }

            setMessageType('image');
            // Thumbnail already generated in handleFileSelect
          };
          img.onerror = () => {
            alert(t('HC.Error.InvalidImage'));
            setInputFileName(t('HC.Error.InvalidImage'));
            setFile(undefined);
            setGeneratedThumbnail(null); // Clear thumbnail on error
          };
          img.src = URL.createObjectURL(file);
        } else {
          setMessageType(result.toString());
        }
      }
    }
  }, [file, selectedRoom?.channel, t]);

  return {
    file,
    fileSize,
    inputFileName,
    messageType,
    generatedThumbnail,
    isHEIC,
    setFile,
    setFileSize,
    setInputFileName,
    setMessageType,
    setGeneratedThumbnail,
    uploadFile,
    uploadMediaFallback,
    handleFileSelect,
    removeFile,
  };
};
